import {Signal} from "signals";
import {IIntegrationWrapper, IWrapperMediator} from "slotworks/integration/revolver/wrapper/wrapper-mediator";
import SettingsModel from "slotworks/model/settings/settings-model";
import RevolverModel from "../revolver/model/revolver-model";
import {InitRecordSchema} from "../revolver/schema/init-record-schema";
import {SpinResponseSchema} from "../revolver/schema/spin-record-schema";
import {WrapperEvent} from "../revolver/wrapper/WrapperEvent";
import * as Logger from "js-logger";

declare const __DEBUG__: boolean;

enum PariplayEvent {
    stopAutobet = "stopAutobet",
    enableSpin = "enableSpin",
    disableSpin = "disableSpin",
    pause = "pause",
    resume = "resume"
}

enum PariplayMessageType {
    OnAppFrameReady = "onAppFrameReady",
    Quit = "quit",
    Help = "help",
    History = "history",
    GameDataLoaded = "gameDataLoaded",
    GameReady = "gameReady",
    RoundStart = "roundStart",
    RoundStarted = "roundStarted",
    TicketReceived = "ticketReceived",
    RoundEnded = "roundEnded",
    Balance = "balance"
}

interface IPariplaySentMessage {
    type: PariplayMessageType;
    data?: any;
    lang: string;
    sender: string;
}

interface PariplayEventData {
    data: {
        type: string;
    };
}

const mediatorEventByPariplayEvent = new Map<PariplayEvent, WrapperEvent>()
    .set(PariplayEvent.disableSpin, WrapperEvent.DisableSpin)
    .set(PariplayEvent.enableSpin, WrapperEvent.EnableSpin)
    .set(PariplayEvent.pause, WrapperEvent.Pause)
    .set(PariplayEvent.resume, WrapperEvent.Resume)
    .set(PariplayEvent.stopAutobet, WrapperEvent.StopAutobet);

export class PariplayWrapper implements IIntegrationWrapper, IWrapperMediator {
    public events = new Signal();

    public constructor() {
        window.addEventListener("message", this.onMessage);
    }

    public dispose = () => {
        window.removeEventListener("message", this.onMessage);
        this.events.removeAll();
        this.events = null;
    }


    // IWrapperMediator implementation
    public init = () => {
        this.sendMessage(PariplayMessageType.OnAppFrameReady);
    }

    public quit = () => {
        this.sendMessage(PariplayMessageType.Quit, {
            reason: "Ribbon"
        });
    }

    public gameDataLoaded = (response: InitRecordSchema) => {
        this.sendMessage(PariplayMessageType.GameDataLoaded, {
            success: true,
            Status: response,
            Balance: response.USER.balance,
            GameCode: this.getGameId(),
        });
    }

    public gameLoaded = () => {
        this.sendMessage(PariplayMessageType.GameReady);
    }

    public roundStart = (totalBet: number) => {
        this.sendMessage(PariplayMessageType.RoundStart, {
            totalBet
        });
    }

    public roundStarted = (spinId: number, balanceBefore: number) => {
        this.sendMessage(PariplayMessageType.RoundStarted, {
            id: spinId,
            balanceBefore,
        });
    }

    public spinResponseReceived = (response: SpinResponseSchema) => {
        this.sendMessage(PariplayMessageType.TicketReceived, {
            ...response.RESULTS.additionalData
        });

        this.sendMessage(PariplayMessageType.RoundEnded, {
            id: response.RESULTS.round_id,
            win: response.RESULTS.total_win_cash,
            balanceAfter: response.USER.balance
        });
    }

    public balanceUpdated = (balance: number) => {
        this.sendMessage(PariplayMessageType.Balance, {
            amount: balance
        });
    }

    public reportPaused = () => {}
    public roundEnd = (win: number, bet: number, balance: number) => {}
    public autoplayStart = () => {};
    public autoplayEnd = () => {};

    // Send/Receive data
    private sendMessage = (type: PariplayMessageType, data?: any) => {
        const message: IPariplaySentMessage = {
            sender: this.getGameId(),
            lang: SettingsModel.language || "en",
            data,
            type
        };

        this.log(type, message);
        window.parent.postMessage(message, "*");
    }

    private onMessage = (eventData: PariplayEventData) => {
        if (!eventData || !eventData.data || !eventData.data.type) {
            if (__DEBUG__) {
                this.warn("Incomplete event received from Pariplay:", eventData);
            }

            return;
        }

        const event = mediatorEventByPariplayEvent.get(eventData.data.type as PariplayEvent);

        if (event) {
            this.events.dispatch(event);
        }
        else if (__DEBUG__) {
            this.warn("Unknown message from Pariplay:", eventData.data.type, eventData);
        }
    }

    private getGameId = () => RevolverModel.getGameCode() || RevolverModel.getGame();

    private log = (...args: any[]) => {
        Logger.info("%c Pariplay Wrapper ", "background: #ffeb3b; color: #000", ...args);
    }

    private warn = (...args: any[]) => {
        Logger.warn("%c Pariplay Wrapper ", "background: #ffeb3b; color: #000", ...args);
    }
}



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/integration/pariplay/PariplayWrapper.ts